/**
 * Search results page. Renders the existing marketplace product grid.
 * The backend ranks part-number matches first which is exactly the
 * demographic's buying intent.
 */
import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { BookmarkPlus, Check } from "lucide-react";
import { api, extractApiError } from "../lib/api";
import type { Product } from "../lib/types";
import { ProductCard } from "../components/marketplace/ProductCard";
import { Button } from "../components/ui/Button";
import { useAuth } from "../contexts/AuthContext";

interface SearchResponse {
  query: string;
  total: number;
  results: Product[];
}

export default function Search() {
  const [params] = useSearchParams();
  const q = params.get("q") ?? "";
  const fitment = params.get("fitment") ?? "";
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = React.useState<SearchResponse | null>(null);
  const [loading, setLoading] = React.useState(false);

  // Phase 19 — "Save this search" CTA state. Resets when query changes.
  const [saveBusy, setSaveBusy] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  React.useEffect(() => {
    setSaved(false);
    setSaveError(null);
  }, [q, fitment]);

  const saveSearch = async () => {
    if (!user) {
      // Match Phase 16 convention: ?redirect= rather than ?next=.
      const back = `/search?q=${encodeURIComponent(q)}${fitment ? `&fitment=${encodeURIComponent(fitment)}` : ""}`;
      navigate(`/login?redirect=${encodeURIComponent(back)}`);
      return;
    }
    setSaveBusy(true);
    setSaveError(null);
    try {
      await api.post("/me/watchlist", {
        name: q ? `Search: ${q}` : "Saved search",
        q: q || undefined,
        fitment_filter: fitment || undefined,
        notify_email: true,
      });
      setSaved(true);
    } catch (err) {
      setSaveError(extractApiError(err));
    } finally {
      setSaveBusy(false);
    }
  };

  React.useEffect(() => {
    if (!q.trim()) {
      setData({ query: q, total: 0, results: [] });
      return;
    }
    setLoading(true);
    (async () => {
      try {
        const r = await api.get<SearchResponse>("/search", { params: { q } });
        setData(r.data);
      } finally {
        setLoading(false);
      }
    })();
  }, [q]);

  return (
    <div className="mx-auto max-w-7xl px-4 md:px-8 py-8 md:py-12">
      <header className="mb-8">
        <p className="text-ink-muted text-sm uppercase tracking-wider mb-2">Search</p>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight mb-2">
          Results for <span className="text-brand">“{q}”</span>
        </h1>
        <p className="text-ink-muted">
          {loading ? "Searching…" : `${data?.total ?? 0} match${(data?.total ?? 0) === 1 ? "" : "es"}. Part-number matches first.`}
        </p>
        {/* Phase 19 — Save this search. */}
        {q.trim() && (
          <div className="mt-4 flex items-center gap-3 flex-wrap">
            <Button
              variant={saved ? "outline" : "primary"}
              size="md"
              onClick={saveSearch}
              disabled={saveBusy || saved}
              leadingIcon={
                saved ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <BookmarkPlus className="h-4 w-4" />
                )
              }
              data-testid="save-search-btn"
            >
              {saved ? "Saved ✓" : saveBusy ? "Saving…" : "Save this search"}
            </Button>
            {saveError && (
              <span
                className="text-sm text-red-700"
                data-testid="save-search-error"
              >
                {saveError}
              </span>
            )}
          </div>
        )}
      </header>

      {loading && (
        <div className="py-16 text-center">
          <div className="h-10 w-10 rounded-full border-4 border-surface-border border-t-brand animate-spin mx-auto" />
        </div>
      )}

      {!loading && data && data.results.length === 0 && (
        <div
          data-testid="search-empty"
          className="rounded-2xl border border-surface-border bg-white p-10 text-center"
        >
          <p className="text-ink font-semibold mb-1">No parts match that search.</p>
          <p className="text-ink-muted text-sm">
            Try the exact OEM number, or the make and model.
          </p>
        </div>
      )}

      {!loading && data && data.results.length > 0 && (
        <div
          data-testid="search-grid"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
        >
          {data.results.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      )}
    </div>
  );
}
